import React from 'react';
import { toast } from 'sonner';
import { Link2, Loader2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { adminApi } from '../../../lib/adminAuth';
import EmailChips from './EmailChips';
import { copyShareLink, errMsg, publicBase } from './docUtils';

export default function SendDocDialog({ open, onClose, doc, kind, defaults, onSent }) {
  const base = kind === 'contract' ? '/api/admin/contracts' : '/api/admin/proposals';
  const [to, setTo] = React.useState([]);
  const [cc, setCc] = React.useState([]);
  const [sender, setSender] = React.useState('');
  const [senders, setSenders] = React.useState([]);
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open || !doc) return;
    setTo(doc.recipients || []);
    setCc(doc.cc?.length ? doc.cc : defaults?.default_cc || []);
    setSender(doc.sender || defaults?.default_sender || '');
    setSubject(''); setMessage('');
    adminApi.get('/api/admin/email/gmail/accounts').then((r) => {
      const list = (r.data?.accounts || []).filter((a) => a.connected).map((a) => a.email);
      setSenders(list);
    }).catch(() => setSenders([]));
  }, [open, doc, defaults]);

  const send = async () => {
    if (!to.length) { toast.error('Add at least one recipient'); return; }
    setBusy(true);
    try {
      const r = await adminApi.post(`${base}/${doc._id}/send`, { to, cc, sender, subject: subject || null, message: message || null, public_base_url: publicBase() }, { timeout: 60000 });
      toast.success(r.data.message);
      onSent?.(); onClose();
    } catch (e) { toast.error(errMsg(e, 'Could not send')); }
    finally { setBusy(false); }
  };

  const senderOptions = Array.from(new Set([sender, ...senders].filter(Boolean)));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="send-doc-dialog">
        <DialogHeader><DialogTitle>Send {kind === 'contract' ? 'contract for signature' : 'proposal'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">To</label>
            <EmailChips value={to} onChange={setTo} testId="send-to" />
            <p className="text-[11px] text-slate-400 mt-1">Pre-filled from the client&apos;s contacts. Edits are remembered for this client.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">CC</label>
            <EmailChips value={cc} onChange={setCc} testId="send-cc" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Send from</label>
            <select value={sender} onChange={(e) => setSender(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="send-from">
              {senderOptions.map((s) => <option key={s} value={s}>{s}{senders.includes(s) ? '' : ' (not connected)'}</option>)}
            </select>
          </div>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (leave blank for the default)" data-testid="send-subject" />
          <Textarea value={message} rows={4} onChange={(e) => setMessage(e.target.value)} placeholder="Message (leave blank for the default note)" data-testid="send-message" />
          <p className="text-[11px] text-slate-500">The PDF is attached and the email has a button to review and sign online.</p>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={async () => { const l = await copyShareLink(`${base}/${doc._id}/share-link`); if (l) { onSent?.(); onClose(); } }} data-testid="send-copy-link">
            <Link2 className="w-4 h-4 mr-1.5" /> Copy sign link instead
          </Button>
          <Button onClick={send} disabled={busy} className="bg-gray-900 hover:bg-gray-800" data-testid="send-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
